import { match } from 'ts-pattern';
import { z } from 'zod';

const ServiceProviderLowerCaseSchema = z.enum([
  'nbn',
  'uniti',
  'vision',
  'vostronet',
]);
export type ServiceProviderLowerCase = z.infer<
  typeof ServiceProviderLowerCaseSchema
>;

const AddressSchema = z.object({
  postcode: z.string(),
  stateTerritoryCode: z.string(),
});

const locationDetailsSchema = z.object({
  formattedAddress: z.string(),
  address: AddressSchema,
});

const TechnologyShortCodeSchema = z.enum([
  'FTTP',
  'HFC',
  'FTTN',
  'FTTB',
  'FTTC',
  'FTF',
  'Wireless',
  'Opticomm',
]);

export type TechnologyShortCode = z.infer<typeof TechnologyShortCodeSchema>;

const serviceDetailsSchema = z.object({
  locationId: z.string(),
  serviceClass: z.number(),
  activeService: z.number(),
  locationDetails: locationDetailsSchema,
  technologyShortCode: TechnologyShortCodeSchema,
  newDevelopmentsChargeApplies: z.boolean(),
  technology: TechnologyShortCodeSchema.optional(), //FIXME: Remove this optional field once we go live
});

export type ServiceDetails = z.infer<typeof serviceDetailsSchema>;

// Simplified schema - We only care if NBN exists or not
export const ServiceQualificationSummarySchema = z.object({
  priority: z.array(ServiceProviderLowerCaseSchema),
  availablePlanCategories: z.array(z.string()), //NOTE: We just care if one of the values is 'EXETEL_RESIDENTIAL'
  nbn: serviceDetailsSchema.optional(),
  uniti: serviceDetailsSchema.optional(),
  vision: serviceDetailsSchema.optional(),
  vostronet: serviceDetailsSchema.optional(),
});

export const ServiceQualificationResponseSchema = z.object({
  sqSummary: ServiceQualificationSummarySchema,
});

export type ServiceQualificationResponse = z.infer<
  typeof ServiceQualificationResponseSchema
>;

export type ServiceQualificationSummary = z.infer<
  typeof ServiceQualificationSummarySchema
>;

export type ServiceQualificationResult =
  | { type: 'serviceable'; serviceDetails: ServiceDetails }
  | { type: 'superloop'; serviceDetails: ServiceDetails }
  | { type: 'unserviceable'; serviceDetails: undefined };

export const processServiceQualification = (
  data: ServiceQualificationSummary
): ServiceQualificationResult => {
  return (
    match(data)
      // If EXETEL_RESIDENTIAL is in the array and NBN exists, we can service on Exetel
      .when(
        (sq) =>
          sq.availablePlanCategories.includes('EXETEL_RESIDENTIAL') &&
          sq.nbn !== undefined &&
          // If NBN is the first/highest priority, we can service on Exetel - Priority is based on the order of the array
          sq.priority[0] === 'nbn',
        (sq) => ({
          type: 'serviceable' as const,
          serviceDetails: sq.nbn!,
        })
      )
      // If previous check is not truthy(line 82 to 91) and availablePlanCategories array includes residential, totalbiz, superbiz and EXETEL_RESIDENTIAL, send over to Superloop
      // We are including exetel residential for the case if availablePlanCategories includes EXETEL_RESIDENTIAL and nbn is not priority we have to send it to Superloop.
      .when(
        (sq) =>
          sq.availablePlanCategories.some((category) =>
            [
              'residential',
              'totalbiz',
              'superbiz',
              'EXETEL_RESIDENTIAL',
            ].includes(category)
          ),
        (sq) => ({
          type: 'superloop' as const,
          serviceDetails: (sq.priority[0] === 'nbn'
            ? sq.nbn
            : sq.priority[0] === 'uniti'
              ? sq.uniti
              : sq.priority[0] === 'vision'
                ? sq.vision
                : sq.priority[0] === 'vostronet'
                  ? sq.vostronet
                  : undefined) as ServiceDetails,
        })
      )
      // Default case - unserviceable
      .otherwise(() => ({
        type: 'unserviceable' as const,
        serviceDetails: undefined,
      }))
  );
};
